/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useMemo, useState } from 'react';

import { css, cx } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import ArchiveBoxIcon from '@atlaskit/icon/core/archive-box';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import type { MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';

import type { AtlassianTeamUpdateArchiveHydratedAction } from '../../types';
import { isActionResolved, isActionErrored } from '../../utils/action-state';
import { CardFooter } from '../card-footer';
import messages from '../messages';

import { TeamItem } from './team-item';

const INITIAL_VISIBLE_COUNT = 6;

const styles = cssMap({
	cardContent: {
		position: 'relative',
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
	},
	showMoreOrLessButton: {
		paddingBlock: token('space.050'),
		paddingInline: token('space.200'),
		borderRadius: token('radius.xxlarge'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		boxShadow: token('elevation.shadow.overlay'),
		backgroundColor: token('elevation.surface'),
		pointerEvents: 'auto',
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
		'&:active': {
			backgroundColor: token('elevation.surface.pressed'),
		},
	},
	showLessButtonContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		paddingBlock: token('space.100'),
	},
	showMoreOrLessButtonLoading: {
		opacity: token('opacity.disabled'),
	},
});

const blurGradient = css({
	position: 'absolute',
	bottom: 0,
	left: 0,
	right: 0,
	height: '48px',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	paddingBottom: token('space.100'),
	background: `linear-gradient(to bottom, transparent 0%, ${token('elevation.surface')} 70%)`,
	pointerEvents: 'none',
});

type ArchiveTeamsCardListViewProps = {
	teams: AtlassianTeamUpdateArchiveHydratedAction[];
	onSelectTeam: (team: AtlassianTeamUpdateArchiveHydratedAction) => void;
	onRemoveTeam: (team: AtlassianTeamUpdateArchiveHydratedAction) => Promise<void>;
	getStatus: (action: MessageAction) => string;
	onArchive: (teams: AtlassianTeamUpdateArchiveHydratedAction[]) => void;
	actionsCurrentlyLoading?: string[];
	isReadOnly?: boolean;
};

export const ArchiveTeamsCardListView = ({
	teams,
	onSelectTeam,
	onRemoveTeam,
	onArchive,
	getStatus,
	actionsCurrentlyLoading = [],
	isReadOnly = false,
}: ArchiveTeamsCardListViewProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const [isShowingAll, setIsShowingAll] = useState(false);
	const { sendAnalyticsEvent } = useAnalytics();
	const visibleTeams = useMemo(
		() => (isShowingAll ? teams : teams.slice(0, INITIAL_VISIBLE_COUNT)),
		[teams, isShowingAll],
	);
	const hiddenCount = teams.length - INITIAL_VISIBLE_COUNT;
	const actionableTeamsCount = teams.filter(
		(team) => !isActionResolved(team, getStatus) && !isActionErrored(team, getStatus),
	).length;

	const fireButtonClickEvent = useCallback(
		(actionSubjectId: 'showMoreCleanUpTeams' | 'showLessCleanUpTeams') => {
			sendAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId,
			});
		},
		[sendAnalyticsEvent],
	);

	const teamItemHoverActions = useMemo(
		() => [
			{
				icon: ArrowRightIcon,
				label: formatMessage(messages.moreInfoTooltip),
				onClick: (team: AtlassianTeamUpdateArchiveHydratedAction) => onSelectTeam(team),
			},
			{
				icon: ArchiveBoxIcon,
				label: formatMessage(messages.archiveTeamButton),
				onClick: (team: AtlassianTeamUpdateArchiveHydratedAction) => onArchive([team]),
			},
		],
		[formatMessage, onArchive, onSelectTeam],
	);
	const teamItemActions = useMemo(
		() => [
			{
				icon: CrossIcon,
				label: formatMessage(messages.removeLabel),
				onClick: async (team: AtlassianTeamUpdateArchiveHydratedAction) => await onRemoveTeam(team),
			},
		],
		[formatMessage, onRemoveTeam],
	);

	return (
		<Box>
			<Box xcss={styles.cardContent}>
				<Stack>
					{visibleTeams.map((team) => (
						<TeamItem
							key={team.data.teamId}
							team={team}
							onClick={() => onSelectTeam(team)}
							actions={teamItemActions}
							hoverActions={teamItemHoverActions}
							isArchiving={actionsCurrentlyLoading.includes(team.data.teamId)}
							hasErrored={getStatus(team) === 'errored'}
						/>
					))}
				</Stack>
				{hiddenCount > 0 &&
					(!isShowingAll ? (
						<div css={blurGradient}>
							<Pressable
								xcss={cx(
									styles.showMoreOrLessButton,
									actionsCurrentlyLoading.length > 0 && styles.showMoreOrLessButtonLoading,
								)}
								onClick={() => {
									fireButtonClickEvent('showMoreCleanUpTeams');
									setIsShowingAll(true);
								}}
								isDisabled={actionsCurrentlyLoading.length > 0}
							>
								<Text size="small" weight="medium" color="color.text.subtle">
									{formatMessage(messages.showMore, { count: hiddenCount })}
								</Text>
							</Pressable>
						</div>
					) : (
						<Box xcss={styles.showLessButtonContainer}>
							<Pressable
								xcss={cx(
									styles.showMoreOrLessButton,
									actionsCurrentlyLoading.length > 0 && styles.showMoreOrLessButtonLoading,
								)}
								onClick={() => {
									fireButtonClickEvent('showLessCleanUpTeams');
									setIsShowingAll(false);
								}}
								isDisabled={actionsCurrentlyLoading.length > 0}
							>
								<Text size="small" weight="medium" color="color.text.subtle">
									{formatMessage(messages.showLess, { count: hiddenCount })}
								</Text>
							</Pressable>
						</Box>
					))}
			</Box>
			{!isReadOnly && (
				<CardFooter>
					<Inline alignInline="end">
						<Button
							appearance="primary"
							onClick={() => onArchive(teams)}
							iconBefore={ArchiveBoxIcon}
							isDisabled={actionsCurrentlyLoading.length > 0 || actionableTeamsCount === 0}
						>
							{formatMessage(messages.archiveButton, {
								teamCount: actionableTeamsCount,
							})}
						</Button>
					</Inline>
				</CardFooter>
			)}
		</Box>
	);
};
